/**
 * Profile slice (SCRUM-1024) — createSlice/createAppAsyncThunk migration of
 * profileReducer + profileAction. Boy-scout migration under the bare-RTK standard
 * (docs/Client_State_And_Data_Fetching_Standard.md); mirrors clientSlice.js
 * (SCRUM-1017) and usersSlice.js.
 *
 * profileAction.js re-exports everything here (shim), so ProfileContainer and the
 * four Blog containers are unchanged.
 *
 * CROSS-SLICE COUPLING — the reason three action types stay legacy strings:
 *  - GET_PROFILE and PROFILE_LOADING are ALSO handled by authReducer, and
 *    layouts/PasswordModalContainer reads `s.auth.profile`. Converting them to
 *    slice actions would silently stop the auth copy from updating.
 *  - CLEAR_MESSAGE is handled by TWELVE reducers (every slice with a `message`);
 *    `clearMessage()` must keep emitting the bare string or the other slices stop
 *    clearing. This is pinned by reducers/siteReducer.test.js.
 * Same escape hatch as clientSlice.js's GET_CLIENTS_BY_COUNTRYCODE.
 *
 * Known dead state (unchanged by this migration): authReducer sets `loading: true`
 * on PROFILE_LOADING and only clears it on GET_PROFILE, so a failed load leaves
 * `auth.loading` true. No component reads `auth.loading` — flagged, not fixed here.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { Dispatch, PayloadAction } from "@reduxjs/toolkit";
import type { AxiosResponse } from "axios";
import type { MessageResponse, ProfileResponse } from "../../../../shared/apiTypes";
import api from "../api";
import createAppAsyncThunk from "../createAppAsyncThunk";
import {
  GET_PROFILE,
  PROFILE_LOADING,
  CLEAR_CURRENT_PROFILE,
  CLEAR_MESSAGE,
} from "./types";

type ProfileState = {
  // `any`, as in the JSDoc it replaces: SiteBlogContainer.tsx and
  // SitePostsContainer.jsx read `profile.name` inside a closure after an
  // outer `profile?.name` check, which a typed profile reds. It narrows when
  // those containers convert.
  profile: any;
  loading: boolean;
  loadError: boolean;
  message: string;
};

const initialState: ProfileState = {
  profile: null,
  loading: false,
  // SCRUM-1024: raised when the fetch failed, so the UI can tell "no profile
  // loaded" apart from "an empty profile loaded fine".
  loadError: false,
  message: "",
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/**
 * SCRUM-1024: the old hand-written version dispatched the SUCCESS action with
 * `payload: {}` on failure. `{}` is truthy, so the form hydrated every field to ""
 * and the spinner cleared — a failed load was indistinguishable from an empty
 * profile, and saving that form overwrote the real record. The failure now lands on
 * `.rejected` (below): `profile` is left untouched and `loadError` goes up.
 */
export const getCurrentProfile = createAppAsyncThunk(
  "profile/getCurrent",
  async (_arg: void, { dispatch }) => {
    // Cross-slice: authReducer has cases for both of these legacy strings.
    dispatch({ type: PROFILE_LOADING });
    const res: AxiosResponse<ProfileResponse> = await api.get("/api/profile/profile");
    dispatch({ type: GET_PROFILE, payload: res.data });
    return res.data;
  }
);

/**
 * NOTE: a failed save deliberately still RESOLVES. Callers dispatch it
 * fire-and-forget and the failure surfaces as a field-level error via the central
 * hub — pinned by profileContainer.realBehavior.test.jsx. `.unwrap()` is available
 * for a caller that wants to branch on the outcome.
 */
export const createProfile = createAppAsyncThunk(
  "profile/create",
  async (profileData: Record<string, unknown>) => {
    const res: AxiosResponse<MessageResponse> = await api.put("/api/profile/profile", profileData);
    return res.data;
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // A failed load must NOT touch `profile`: it stays null, or keeps a
      // previously loaded profile through a failed refetch.
      .addCase(getCurrentProfile.rejected, (state) => {
        state.loading = false;
        state.loadError = true;
      })
      .addCase(createProfile.fulfilled, (state, action) => {
        state.message = action.payload?.msg;
      })
      // --- legacy strings (see the header): shared with authReducer / every
      // other slice that owns a `message`.
      .addCase(PROFILE_LOADING, (state) => {
        state.loading = true;
        state.loadError = false;
      })
      .addCase(GET_PROFILE, (state, action: PayloadAction<ProfileResponse, typeof GET_PROFILE>) => {
        state.profile = action.payload;
        state.loading = false;
        state.loadError = false;
      })
      .addCase(CLEAR_CURRENT_PROFILE, (state) => {
        state.profile = null;
      })
      .addCase(CLEAR_MESSAGE, (state) => {
        state.message = "";
      });
  },
});

// Public API keeps the legacy names and the legacy emitted shapes.
/** Bare CLEAR_MESSAGE — twelve reducers listen for it (see the header). */
export const clearMessage = () => (dispatch: Dispatch) =>
  dispatch({ type: CLEAR_MESSAGE });

export default profileSlice.reducer;
